import React, { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import {
  fetchAspirantsByEglise,
  fetchEgliseInfo,
} from "../../services/aspirantService";
import {
  fetchRequirementsByAspirant,
  fetchRequirementsByAspirantWithStatus,
} from "../../services/requirementsService";
import AspirantTable from "./AspirantTable";
import { getFrontFileUrl } from "../../services/apiFront";

function getRowId(a) {
  const raw = a?.aspirant_id ?? a?.id ?? a?.user_id;
  return raw != null ? String(raw) : "undefined";
}

export default function PreuvesEglise() {
  const [aspirants, setAspirants] = useState([]);
  const [requirements, setRequirements] = useState([]); 
  const [egliseId, setEgliseId] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const [checkedAspirant, setCheckedAspirant] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [resubmitFiles, setResubmitFiles] = useState({}); // Fichiers pour re-soumettre les exigences refusées
  const [step, setStep] = useState(1); // 1: sélection aspirant, 2: liste exigences, 3: ajout exigence
  const [currentParcours, setCurrentParcours] = useState(null);
  const [allRequirements, setAllRequirements] = useState([]); // Toutes les exigences avec statut

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
    variant: "success",
  });
  const openModal = (title, message, variant = "success") =>
    setModal({ open: true, title, message, variant });
  const closeModal = () => setModal((m) => ({ ...m, open: false }));

  useEffect(() => {
    (async () => {
      try {
        const me = await api.get("/me");
        setUserRole(me.data?.role || me.data?.user?.role || null);

        const eg = await fetchEgliseInfo();
        setEgliseId(eg.eglise.id);

        const list = await fetchAspirantsByEglise(eg.eglise.id);
        setAspirants(Array.isArray(list) ? list : []);

        setRequirements([]);
      } catch (e) {
        console.error("Init error:", e);
        openModal("Erreur", "Impossible de charger les données initiales.", "error");
      }
    })();
  }, []);

  const chefGuide = useMemo(
    () => aspirants.filter((a) => {
      // Filtrer par parcours
      if (a.parcours !== "Chef Guide") return false;
      // Exclure les élèves où matricule est en attente (null, undefined, vide, ou "en attente")
      const matricule = a.matricule;
      return matricule && matricule.trim() !== "" && matricule.toLowerCase() !== "en attente";
    }),
    [aspirants]
  );
  const syl = useMemo(
    () => aspirants.filter((a) => {
      // Filtrer par parcours
      if (a.parcours !== "Senior Youth Leader") return false;
      // Exclure les élèves où matricule est en attente (null, undefined, vide, ou "en attente")
      const matricule = a.matricule;
      return matricule && matricule.trim() !== "" && matricule.toLowerCase() !== "en attente";
    }),
    [aspirants]
  );

  const handleFileChange = (requirementId, file) => {
    setUploadedFiles((prev) => ({ ...prev, [requirementId]: file }));
  };

  const handleResubmitFileChange = (requirementId, file) => {
    setResubmitFiles((prev) => ({ ...prev, [requirementId]: file }));
  };

  /** ✅ appelé quand on clique un NOM dans la table */
  const handlePickAspirant = async (asp) => {
    if (!asp) return;

    // Détermine parcours de élève cliqué
    const parcours = asp.parcours === "Chef Guide" ? "CG" : "SYL";

    // Reset états liés au Step 2
    setCheckedAspirant(asp);
    setCurrentParcours(parcours);
    setUploadedFiles({});
    setResubmitFiles({});
    setRequirements([]);
    setAllRequirements([]);

    // Charge TOUTES les requirements de cet élève pour son parcours (avec statut)
    try {
      const aspirantId = asp.aspirant_id ?? asp.id ?? asp.user_id;
      const data = await fetchRequirementsByAspirantWithStatus(aspirantId, {
        parcours,
      });

      // Normalisation : certaines APIs renvoient requirement_id, d'autres id
      const list = (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        _rid: r.requirement_id ?? r.id, // ID sûr pour la suite
      }));

      setAllRequirements(list);
    } catch (e) {
      console.error("Erreur chargement requirements par élève:", e?.response || e);
      openModal(
        "Erreur",
        "Impossible de charger les exigences de l'élève sélectionné.",
        "error"
      );
    }

    setStep(2); // passer au step 2 (liste des exigences)
  };

  const renderStep1 = () => {
    const checkedMap = checkedAspirant ? { [getRowId(checkedAspirant)]: true } : {};
    return (
      <>
        <h4 className="mb-3">Sélectionnez un élève</h4>
        <div className="row g-3">
          <div className="col-12 col-lg-6">
            <AspirantTable
              title="Chef Guide"
              aspirants={chefGuide}
              checkedAspirants={checkedMap}
              singleSelect
              egliseId={egliseId}
              userRole={userRole}
              onPick={handlePickAspirant}
              showActionColumn={false}
              hideAgeAndPhone={true}
            />
          </div>
          <div className="col-12 col-lg-6">
            <AspirantTable
              title="Senior Youth Leader"
              aspirants={syl}
              checkedAspirants={checkedMap}
              singleSelect
              egliseId={egliseId}
              userRole={userRole}
              onPick={handlePickAspirant}
              showActionColumn={false}
              hideAgeAndPhone={true}
            />
          </div>
        </div>
      </>
    );
  };

  // Fonction pour gérer la re-soumission d'une exigence refusée
  const handleResubmit = async (requirement) => {
    if (!checkedAspirant || !resubmitFiles[requirement._rid]) {
      openModal("Info", "Veuillez sélectionner un nouveau fichier.", "error");
      return;
    }

    const aspirantId = checkedAspirant.aspirant_id ?? checkedAspirant.id ?? checkedAspirant.user_id;
    const formData = new FormData();
    formData.append("aspirant_id", aspirantId);
    formData.append("requirement_id", requirement._rid);
    formData.append("file", resubmitFiles[requirement._rid]);
    formData.append("status", "en_attente");

    try {
      await api.post("/aspirants/requirements", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      openModal("Succès", `L'exigence "${requirement.title}" a été re-soumise avec succès.`, "success");
      // Recharger les données
      await handlePickAspirant(checkedAspirant);
      // Réinitialiser le fichier
      setResubmitFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[requirement._rid];
        return newFiles;
      });
    } catch (err) {
      console.error("Erreur re-soumission:", err);
      const errorMessage = err?.response?.data?.error || err?.message || "Erreur lors de la re-soumission";
      openModal("Erreur", errorMessage, "error");
    }
  };

  // Fonction pour rendre un tableau d'exigences (similaire au district)
  const renderRequirementsTable = (title, rows, badgeColor, allowResubmit = false) => {
    return (
      <div className="card shadow-sm mt-3 mt-md-4 rounded-0">
        <div className={`card-header bg-${badgeColor} text-white py-2 py-md-3 rounded-0`}>
          <h5 className="mb-0 fs-6 fs-md-5" style={{ fontSize: "0.85rem" }}>{title}</h5>
        </div>
        <div className="card-body p-0">
          {rows.length === 0 ? (
            <div className="p-4 text-center text-muted">
              Aucune exigence trouvée.
            </div>
          ) : (
            <>
              {/* Version desktop : tableau */}
              <div
                className="table-responsive d-none d-md-block"
                style={{
                  overflowX: "auto",
                  overflowY: "auto",
                  maxHeight: "60vh",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <table className="table table-hover table-sm align-middle mb-0" style={{ borderRadius: 0 }}>
                  <thead className="table-light" style={{ borderRadius: 0 }}>
                    <tr>
                      <th className="text-nowrap">Exigence</th>
                      <th className="text-nowrap">Statut</th>
                      <th className="text-nowrap">Fichier</th>
                      <th className="text-nowrap">Soumise le</th>
                      <th className="text-nowrap">Commentaire</th>
                      {allowResubmit && <th className="text-nowrap">Action</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const statusDisplay = (r.status_display || "").toLowerCase();
                      // Utiliser la même logique que le filtrage : refusée seulement si avec commentaire
                      const isValidated = statusDisplay === "valide" || r.is_validated === 1;
                      const isRefusedStatus = statusDisplay === "refuse" || 
                                            statusDisplay === "rejete" || 
                                            statusDisplay === "rejeté" ||
                                            r.is_validated === 0;
                      const hasComment = r.comment && r.comment.trim().length > 0;
                      const isRefused = isRefusedStatus && hasComment;
                      
                      return (
                        <tr key={r._rid}>
                          {/* Exigence */}
                          <td style={{ maxWidth: 300, wordWrap: "break-word" }}>
                            <div 
                              className="fw-semibold" 
                              style={{ 
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                lineHeight: "1.4"
                              }}
                            >
                              {r.title || "—"}
                            </div>
                            {r.description && r.description !== r.title && (
                              <div 
                                className="text-muted small" 
                                style={{ 
                                  wordBreak: "break-word",
                                  whiteSpace: "normal",
                                  lineHeight: "1.4",
                                  marginTop: "4px"
                                }}
                              >
                                {r.description}
                              </div>
                            )}
                          </td>
                          
                          {/* Statut */}
                          <td>
                            {isValidated ? (
                              <span className="badge bg-success">
                                <i className="bi bi-check-circle me-1"></i>Validée
                              </span>
                            ) : isRefused ? (
                              <span className="badge bg-danger">
                                <i className="bi bi-x-circle me-1"></i>Refusée
                              </span>
                            ) : (
                              <span className="badge bg-warning text-dark">
                                <i className="bi bi-hourglass-split me-1"></i>En attente
                              </span>
                            )}
                          </td>
                          
                          {/* Fichier */}
                          <td>
                            {r.justification_file ? (
                              <a
                                href={getFrontFileUrl(r.justification_file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-primary btn-sm text-nowrap"
                                title="Ouvrir le document"
                              >
                                <i className="bi bi-eye me-1"></i>Voir le doc
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          
                          {/* Date */}
                          <td className="text-nowrap">
                            {r.validated_at
                              ? new Date(r.validated_at).toLocaleDateString("fr-FR")
                              : r.created_at
                              ? new Date(r.created_at).toLocaleDateString("fr-FR")
                              : "—"}
                          </td>
                          
                          {/* Commentaire */}
                          <td>
                            <div className="text-truncate" style={{ maxWidth: 260 }}>
                              {r.comment || <span className="text-muted">—</span>}
                            </div>
                          </td>
                          
                          {/* Action pour re-soumettre (uniquement si allowResubmit) */}
                          {allowResubmit && (
                            <td>
                              <div className="d-flex flex-column gap-2">
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleResubmitFileChange(r._rid, e.target.files[0])}
                                  className="form-control form-control-sm"
                                  style={{ fontSize: "0.8rem" }}
                                />
                                {resubmitFiles[r._rid] && (
                                  <button
                                    className="btn btn-warning btn-sm"
                                    onClick={() => handleResubmit(r)}
                                    title="Re-soumettre avec le nouveau fichier"
                                  >
                                    <i className="bi bi-arrow-repeat me-1"></i>Re-soumettre
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Version mobile : cartes */}
              <div className="d-md-none p-2">
                {rows.map((r) => {
                  const statusDisplay = (r.status_display || "").toLowerCase();
                  // Utiliser la même logique que le filtrage : refusée seulement si avec commentaire
                  const isValidated = statusDisplay === "valide" || r.is_validated === 1;
                  const isRefusedStatus = statusDisplay === "refuse" || 
                                        statusDisplay === "rejete" || 
                                        statusDisplay === "rejeté" ||
                                        r.is_validated === 0;
                  const hasComment = r.comment && r.comment.trim().length > 0;
                  const isRefused = isRefusedStatus && hasComment;
                  
                  return (
                    <div key={r._rid} className="card mb-2 shadow-sm rounded-0">
                      <div className="card-body p-2">
                        <div className="mb-2">
                          <div className="fw-semibold mb-1" style={{ 
                                fontSize: "0.8rem",
                                wordBreak: "break-word",
                                whiteSpace: "pre-wrap",
                                lineHeight: "1.2",
                                letterSpacing: "0.01em"
                              }}>
                            {r.title || "—"}
                          </div>
                          {r.description && r.description !== r.title && (
                            <div 
                              className="text-muted mb-0"
                              style={{ 
                                fontSize: "0.75rem",
                                wordBreak: "break-word",
                                whiteSpace: "normal",
                                lineHeight: "1.3"
                              }}
                            >
                              {r.description}
                            </div>
                          )}
                        </div>
                        
                        <div className="d-flex flex-column gap-1 mb-1">
                          {r.justification_file && (
                            <a
                              href={getFrontFileUrl(r.justification_file)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-primary btn-sm w-100"
                              style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem" }}
                              title="Ouvrir le document"
                            >
                              <i className="bi bi-eye me-1"></i>Voir le document
                            </a>
                          )}
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar text-muted me-1" style={{ fontSize: "0.75rem" }}></i>
                            <small className="text-muted" style={{ fontSize: "0.7rem" }}>
                              {r.validated_at
                                ? `Validée le ${new Date(r.validated_at).toLocaleDateString("fr-FR")}`
                                : r.created_at
                                ? `Soumise le ${new Date(r.created_at).toLocaleDateString("fr-FR")}`
                                : "—"}
                            </small>
                          </div>
                        </div>
                        
                        {r.comment && (
                          <div className="mt-2 pt-2 border-top">
                            <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "0.7rem" }}>
                              <i className="bi bi-chat-left-text me-1"></i>Commentaire :
                            </small>
                            <div className="text-break" style={{ fontSize: "0.75rem" }}>{r.comment}</div>
                          </div>
                        )}
                        
                        {/* Action pour re-soumettre (uniquement si allowResubmit) */}
                        {allowResubmit && (
                          <div className="mt-2 pt-2 border-top">
                            <small className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: "0.7rem" }}>
                              <i className="bi bi-arrow-repeat me-1"></i>Re-soumettre :
                            </small>
                            <div className="d-flex flex-column gap-2">
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleResubmitFileChange(r._rid, e.target.files[0])}
                                className="form-control form-control-sm"
                                style={{ fontSize: "0.75rem" }}
                              />
                              {resubmitFiles[r._rid] && (
                                <button
                                  className="btn btn-warning btn-sm w-100"
                                  onClick={() => handleResubmit(r)}
                                  style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                                >
                                  <i className="bi bi-arrow-repeat me-1"></i>Re-soumettre
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Step 2: Afficher les exigences validées, en attente et refusées
  const renderStep2 = () => {
    if (!checkedAspirant) return null;

    const parcoursLabel = currentParcours === "CG" ? "Chef Guide" : "Senior Youth Leader";
    const parcoursLabelShort = currentParcours === "CG" ? "CG" : "SYL";
    
    // Séparer les exigences en 3 catégories distinctes
    // Logique : une preuve envoyée par l'église a 3 statuts possibles : en attente, validée, refusée
    const validatedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      // Validées : is_validated = 1 OU status_display = 'valide'
      return r.is_validated === 1 || statusDisplay === "valide";
    });
    
    const refusedReqs = allRequirements.filter((r) => {
      if (!r.justification_file) return false;
      const statusDisplay = (r.status_display || "").toLowerCase();
      // Refusées : UNIQUEMENT si explicitement refusé par le coordinateur district ET avec un commentaire
      // Si refusée mais sans commentaire, elle sera classée en attente
      const isRefusedStatus = (r.is_validated === 0 || 
                               statusDisplay === "refuse" || 
                               statusDisplay === "rejete" || 
                               statusDisplay === "rejeté");
      // Une exigence refusée doit avoir un commentaire pour être vraiment refusée
      const hasComment = r.comment && r.comment.trim().length > 0;
      return isRefusedStatus && hasComment;
    });
    
    const pendingReqs = allRequirements.filter((r) => {
      // Une exigence est en attente si elle a un fichier ET n'est ni validée ni refusée
      if (!r.justification_file) return false;
      
      const statusDisplay = (r.status_display || "").toLowerCase();
      
      // Validées : is_validated = 1 OU status_display = 'valide'
      const isValidated = r.is_validated === 1 || statusDisplay === "valide";
      
      // Refusées : is_validated = 0 OU status_display contient 'refuse'/'rejete'/'rejeté' ET avec commentaire
      // Si refusée mais sans commentaire, elle est considérée en attente
      const isRefusedStatus = r.is_validated === 0 || 
                              statusDisplay === "refuse" || 
                              statusDisplay === "rejete" || 
                              statusDisplay === "rejeté";
      const hasComment = r.comment && r.comment.trim().length > 0;
      const isRefused = isRefusedStatus && hasComment;
      
      // En attente : toute exigence avec fichier qui n'est NI validée NI refusée (avec commentaire)
      // Cela inclut :
      // - is_validated IS NULL (nouvelle preuve uploadée)
      // - is_validated = undefined
      // - status_display = 'en_attente'
      // - Exigences refusées mais SANS commentaire (elles doivent être en attente)
      // - Toute autre valeur qui n'est ni 'valide' ni 'refuse' avec commentaire
      return !isValidated && !isRefused;
    });

    // Charger les exigences non validées pour le bouton "Ajouter une exigence"
    const loadPendingRequirements = async () => {
      try {
        const aspirantId = checkedAspirant.aspirant_id ?? checkedAspirant.id ?? checkedAspirant.user_id;
        const data = await fetchRequirementsByAspirant(aspirantId, {
          parcours: currentParcours,
          onlyPending: true,
        });

        const list = (Array.isArray(data) ? data : []).map((r) => ({
          ...r,
          _rid: r.requirement_id ?? r.id,
        }));

        setRequirements(list);
        setStep(3); // Passer au step 3 (ajout d'exigence)
      } catch (e) {
        console.error("Erreur chargement requirements:", e?.response || e);
        openModal("Erreur", "Impossible de charger les exigences.", "error");
      }
    };

    return (
      <>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 mb-md-4 gap-2">
          <h4 className="mb-0" style={{ fontSize: "1rem" }}>
            Exigences pour {checkedAspirant.name} - <span className="text-muted" style={{ fontSize: "0.85rem" }}>
              <span className="d-inline d-md-none">{parcoursLabelShort}</span>
              <span className="d-none d-md-inline">{parcoursLabel}</span>
            </span>
          </h4>
          <button
            className="btn btn-primary btn-sm"
            onClick={loadPendingRequirements}
            style={{ fontSize: "0.8rem", padding: "0.25rem 0.5rem" }}
          >
            <i className="bi bi-plus-circle me-1"></i>
            <span className="d-none d-sm-inline">Ajouter une exigence</span>
            <span className="d-sm-none">Ajouter</span>
          </button>
        </div>

        {/* Exigences validées */}
        {renderRequirementsTable(
          `Exigences validées (${validatedReqs.length})`,
          validatedReqs,
          "success"
        )}

        {/* Exigences en attente */}
        {renderRequirementsTable(
          `Exigences en attente de validation (${pendingReqs.length})`,
          pendingReqs,
          "warning"
        )}

        {/* Exigences refusées */}
        {renderRequirementsTable(
          `Exigences refusées (${refusedReqs.length})`,
          refusedReqs,
          "danger",
          true // allowResubmit = true pour permettre la re-soumission
        )}

        <div className="mt-4">
          <button
            className="btn btn-secondary btn-sm w-100 w-md-auto"
            onClick={() => {
              setCheckedAspirant(null);
              setCurrentParcours(null);
              setAllRequirements([]);
              setRequirements([]);
              setStep(1);
            }}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Retour
          </button>
        </div>
      </>
    );
  };

  // Step 3: Page d'ajout d'exigence (upload)
  const renderStep3 = () => {
    const reqs = requirements; // déjà filtrés: parcours + non validés
    const parcoursLabel = currentParcours === "CG" ? "Chef Guide" : "Senior Youth Leader";
    const parcoursLabelShort = currentParcours === "CG" ? "CG" : "SYL";

    return (
      <>
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
          <h4 className="mb-0" style={{ whiteSpace: "nowrap" }}>
            Exigences pour <span className="d-inline d-md-none">{parcoursLabelShort}</span><span className="d-none d-md-inline">{parcoursLabel}</span> actuel
          </h4>
          <button
            className="btn btn-secondary btn-sm w-100 w-md-auto"
            onClick={() => {
              setUploadedFiles({});
              setRequirements([]);
              setStep(2); // Retour à la liste des exigences
            }}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Retour
          </button>
        </div>

        {reqs.length === 0 ? (
          <div className="alert alert-success">
            Toutes les exigences de ce parcours ont déjà été envoyées/validées pour cet élève. 🎉
          </div>
        ) : (
          reqs.map((r) => {
            const idKey = r._rid; // requirement_id sûr
            return (
              <div className="card shadow-sm mb-3 rounded-0" key={idKey}>
                <div className="card-body d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                  <div className="flex-grow-1 me-md-3">
                    <h6 className="mb-1">{r.title}</h6>
                    <p className="mb-0 text-muted small">{r.description}</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(idKey, e.target.files[0])}
                    className="w-100 w-md-auto"
                  />
                </div>
              </div>
            );
          })
        )}

        <div className="d-flex justify-content-end mt-4">
          <button
            className="btn btn-success w-100 w-md-auto"
            onClick={async () => {
              if (!checkedAspirant) return;

              const reqsToSend = requirements.filter((r) => uploadedFiles[r._rid]);
              if (reqsToSend.length === 0) {
                openModal("Info", "Aucun fichier sélectionné.", "error");
                return;
              }

              let ok = 0,
                ko = 0,
                errors = [];
              const aspirantId =
                checkedAspirant.aspirant_id ??
                checkedAspirant.id ??
                checkedAspirant.user_id;

              for (const r of reqsToSend) {
                const formData = new FormData();
                formData.append("aspirant_id", aspirantId);
                formData.append("requirement_id", r._rid); // utiliser l'ID sûr
                formData.append("file", uploadedFiles[r._rid]);
                formData.append("status", "en_attente");
                try {
                  await api.post("/aspirants/requirements", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  ok++;
                } catch (err) {
                  ko++;
                  errors.push(`${r.title}: ${err?.response?.data?.error || err.message}`);
                }
              }

              if (ko === 0)
                openModal("Succès", `${ok} exigences(s) envoyée(s) avec succès.`, "success");
              else if (ok === 0)
                openModal("Échec", `Aucune exigence envoyée.\n${errors.join("\n")}`, "error");
              else
                openModal(
                  "Partiel",
                  `${ok} envoyée(s), ${ko} échec(s).\n${errors.join("\n")}`,
                  "warning"
                );

              // Recharger les données et retourner à la liste
              await handlePickAspirant(checkedAspirant);
            }}
          >
            <i className="bi bi-send me-1"></i>
            Envoyer les données sélectionnées
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="with-sidebar container-fluid py-3 py-md-4 px-3 px-md-4">
      {step === 1 ? renderStep1() : step === 2 ? renderStep2() : renderStep3()}

      {modal.open && (
        <>
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div
                  className={`modal-header ${
                    modal.variant === "success"
                      ? "bg-success"
                      : modal.variant === "warning"
                      ? "bg-warning"
                      : "bg-danger"
                  } text-white`}
                >
                  <h5 className="modal-title">{modal.title}</h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={closeModal}
                  />
                </div>
                <div className="modal-body">
                  <pre className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {modal.message}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className={`btn btn-${
                      modal.variant === "success"
                        ? "success"
                        : modal.variant === "warning"
                        ? "warning"
                        : "danger"
                    }`}
                    onClick={closeModal}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
}
